import { INTERVAL_SECONDS } from '../../config/timeframes.js';
import { safeNumber } from '../../lib/number.js';
import type { Candle, ChartInterval } from '../../types/market.js';
import type { ExternalVenueTicker } from './bybit.js';
import { fetchJson } from './http.js';

interface OkxTickerResponse {
  code: string;
  msg?: string;
  data?: Array<{
    instId: string;
    last: string;
    volCcy24h: string;
    open24h?: string;
    bidPx: string;
    askPx: string;
  }>;
}

interface OkxKlineResponse {
  code: string;
  msg?: string;
  data?: string[][];
}

export const OKX_INTERVAL: Record<ChartInterval, string> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1H',
  '2h': '2H',
  '4h': '4H',
  '1d': '1Dutc',
  '1w': '1Wutc',
};

export function parseOkxCandles(
  rows: string[][],
  interval: ChartInterval,
  serverNow = Date.now(),
): Candle[] {
  const durationMs = INTERVAL_SECONDS[interval] * 1_000;
  return rows
    .map((row) => {
      const openTime = safeNumber(row[0]);
      const closeTime = openTime + durationMs - 1;
      return {
        openTime,
        closeTime,
        open: safeNumber(row[1]),
        high: safeNumber(row[2]),
        low: safeNumber(row[3]),
        close: safeNumber(row[4]),
        volume: safeNumber(row[5]),
        quoteVolume: safeNumber(row[7] ?? row[6]),
        closed: row[8] !== undefined ? row[8] === '1' : closeTime < serverNow,
      } satisfies Candle;
    })
    .filter((candle) => candle.openTime > 0 && candle.close > 0)
    .sort((left, right) => left.openTime - right.openTime);
}

export async function getOkxTicker(baseAsset: string): Promise<ExternalVenueTicker | null> {
  const instId = `${baseAsset}-USDT-SWAP`;
  const response = await fetchJson<OkxTickerResponse>(
    `https://www.okx.com/api/v5/market/ticker?instId=${encodeURIComponent(instId)}`,
  );
  if (response.code !== '0') {
    throw new Error(`OKX market API error ${response.code}: ${response.msg ?? 'unknown error'}`);
  }
  const item = response.data?.[0];
  if (!item) return null;
  const price = safeNumber(item.last);
  const open24h = safeNumber(item.open24h);
  return {
    exchange: 'okx',
    symbol: item.instId,
    price,
    change24h: open24h > 0 ? ((price - open24h) / open24h) * 100 : undefined,
    bid: safeNumber(item.bidPx),
    ask: safeNumber(item.askPx),
  };
}

export async function getOkxCandles(
  symbol: string,
  interval: ChartInterval,
  limit = 300,
  marketKind: 'spot' | 'futures' = 'futures',
  serverNow = Date.now(),
): Promise<Candle[]> {
  const baseAsset = symbol.endsWith('USDT') ? symbol.slice(0, -4) : symbol;
  const instId = marketKind === 'spot' ? `${baseAsset}-USDT` : `${baseAsset}-USDT-SWAP`;
  const response = await fetchJson<OkxKlineResponse>(
    `https://www.okx.com/api/v5/market/candles?instId=${encodeURIComponent(instId)}&bar=${OKX_INTERVAL[interval]}&limit=${Math.min(300, limit)}`,
  );
  if (response.code !== '0') {
    throw new Error(`OKX kline API error ${response.code}: ${response.msg ?? 'unknown error'}`);
  }
  return parseOkxCandles(response.data ?? [], interval, serverNow);
}
